import {
  AvatarStack,
  AvatarTile,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DueLabel,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PriorityMark,
  StatusBadge,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { CalendarIcon, FlagIcon, UserIcon, UserPlusIcon } from "lucide-react";
import { useId, useState } from "react";
import {
  addDays,
  localDay,
  PRIORITIES,
  priorityKey,
  type SeatPeople,
  splitAssignees,
  statusTone,
} from "./model";
import type { Board, TaskCard } from "./view-engine";

// WORK-PERF: the row writes are optimistic now, with every other task write (task-writes.ts);
// WORK-OS's due and priority cells write through its `update`, its quick add through `create`.
export { useTaskWrites } from "./task-writes";

const TRIGGER =
  "inline-flex h-6 items-center rounded-md outline-none transition-colors duration-(--dur-fast) ease-out hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring focus-visible:outline-solid";

/** The status badge as a menu (hidden when the board is not known yet). */
export function StatusMenu({
  board,
  status,
  onChange,
  className,
}: {
  board: Pick<Board, "status_set"> | undefined;
  status: string;
  onChange: (status: string) => void;
  className?: string;
}) {
  const def = board?.status_set.find((s) => s.key === status);
  const label = def?.name ?? status;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Status: ${label}`}
        data-inline="status"
        className={cn(TRIGGER, "px-0.5", className)}
        onClick={(e) => e.stopPropagation()}
      >
        <StatusBadge status={{ label, tone: statusTone(def) }} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-44">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Status</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={status} onValueChange={(v) => onChange(String(v))}>
            {(board?.status_set ?? []).map((s) => (
              <DropdownMenuRadioItem key={s.key} value={s.key} closeOnClick>
                {s.name}
                {s.done ? (
                  <span className="ml-auto text-xs text-muted-foreground">done</span>
                ) : null}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The assignees as a menu of seats: one tile and the name (Linear's shape), a stacked pair with
 * the names on hover when several, a "+" that shows on hover when none. `compact` (a checklist
 * item) shows the tile alone with the name on hover; `withDelegates={false}` offers people only.
 */
export function AssigneeMenu({
  people,
  seatIds,
  title,
  onToggle,
  className,
  compact = false,
  withDelegates = true,
}: {
  people: SeatPeople;
  seatIds: readonly string[];
  /** The task's title, for the menu's accessible name. */
  title: string;
  onToggle: (seatId: string, on: boolean) => void;
  className?: string;
  compact?: boolean;
  withDelegates?: boolean;
}) {
  const { owners, delegates } = splitAssignees(seatIds, people);
  const chosen = [...owners, ...delegates];
  const names = chosen.map((p) => p.full).join(", ");
  const one = chosen.length === 1 ? chosen[0] : undefined;
  const humans = people.all.filter((p) => !p.de && !p.closed);
  const agents = withDelegates ? people.all.filter((p) => p.de && !p.closed) : [];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={chosen.length ? `Assignees of ${title}: ${names}` : `Assign ${title}`}
        data-inline="assignees"
        title={chosen.length ? names : undefined}
        className={cn(
          TRIGGER,
          "min-w-0 max-w-full gap-1.5 px-0.5 text-faint hover:text-foreground",
          chosen.length && "text-muted-foreground",
          !chosen.length &&
            "opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100",
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {one && compact ? (
          one.open ? (
            <UserIcon aria-hidden="true" className="size-4 shrink-0 text-faint" />
          ) : (
            <AvatarTile size="xs" name={one.name} kind={one.de ? "agent" : "person"} />
          )
        ) : one ? (
          <>
            {one.open ? (
              <UserIcon aria-hidden="true" className="size-4 shrink-0 text-faint" />
            ) : (
              <AvatarTile size="sm" name={one.name} kind={one.de ? "agent" : "person"} />
            )}
            <span className={cn("truncate text-xs", one.open && "text-faint")}>
              {one.de ? workerName(one.label) : one.label}
            </span>
            {/* WORKFORCE: a DE assignee is badged. */}
            {one.de && !one.open ? <WorkerBadge kind="de" size="sm" /> : null}
          </>
        ) : chosen.length ? (
          <AvatarStack
            size="sm"
            max={2}
            items={chosen.map((p) => ({ name: p.name, kind: p.de ? "agent" : "person" }))}
          />
        ) : (
          <UserPlusIcon aria-hidden="true" className="size-4" />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Assignee</DropdownMenuLabel>
          {humans.length ? (
            humans.map((p) => (
              <DropdownMenuCheckboxItem
                key={p.seatId}
                checked={chosen.some((c) => c.seatId === p.seatId)}
                onCheckedChange={(on) => onToggle(p.seatId, Boolean(on))}
              >
                <span className={cn("truncate", p.open && "text-muted-foreground")}>
                  {p.open ? p.label : p.name}
                </span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {p.open ? "" : p.hint}
                </span>
              </DropdownMenuCheckboxItem>
            ))
          ) : (
            <DropdownMenuCheckboxItem disabled checked={false}>
              No seats yet
            </DropdownMenuCheckboxItem>
          )}
        </DropdownMenuGroup>
        {agents.length ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel>Delegate (digital employee)</DropdownMenuLabel>
              {agents.map((p) => (
                <DropdownMenuCheckboxItem
                  key={p.seatId}
                  checked={chosen.some((c) => c.seatId === p.seatId)}
                  onCheckedChange={(on) => onToggle(p.seatId, Boolean(on))}
                >
                  <WorkerBadge kind="de" size="sm" />
                  {workerName(p.name)}
                  <span className="ml-auto text-xs text-muted-foreground">{p.hint}</span>
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuGroup>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// WORK-OS: due date and priority in the row (My tasks; Asana's inline cells) ---------------------

/**
 * The due date as a popover: Today, Tomorrow, Next week, a date field and Clear. Reads as the
 * due label (red when overdue); a dateless row shows a calendar glyph on hover.
 */
export function DueMenu({
  task,
  today,
  overdue,
  onChange,
  className,
}: {
  task: Pick<TaskCard, "title" | "due_at">;
  today: string;
  overdue: boolean;
  onChange: (day: string | null) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState("");
  const id = useId();
  const due = task.due_at ? localDay(task.due_at) : null;
  const pick = (d: string | null) => {
    onChange(d);
    setOpen(false);
  };
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setDay(due ?? "");
      }}
    >
      <PopoverTrigger
        aria-label={due ? `Due date of ${task.title}: ${due}` : `Set a due date for ${task.title}`}
        data-inline="due"
        className={cn(
          TRIGGER,
          "min-w-0 gap-1 px-1 text-xs",
          !due &&
            "text-faint opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100",
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {due ? (
          <DueLabel due={due} overdue={overdue} />
        ) : (
          <CalendarIcon aria-hidden="true" className="size-4" />
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60">
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            pick(day || null);
          }}
        >
          <div className="flex flex-wrap gap-1">
            <Button type="button" size="sm" variant="secondary" onClick={() => pick(today)}>
              Today
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => pick(addDays(today, 1))}
            >
              Tomorrow
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => pick(addDays(today, 7))}
            >
              Next week
            </Button>
          </div>
          <label htmlFor={id} className="text-xs text-muted-foreground">
            Due date
          </label>
          <Input id={id} type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          <div className="flex justify-end gap-2">
            {due ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => pick(null)}>
                Clear
              </Button>
            ) : null}
            <Button type="submit" size="sm" disabled={!day}>
              Set
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/** The priority mark as a menu (Urgent … Low, No priority); a flag on hover when none. */
export function PriorityMenu({
  task,
  onChange,
  className,
}: {
  task: Pick<TaskCard, "title" | "priority">;
  onChange: (priority: number) => void;
  className?: string;
}) {
  const key = priorityKey(task.priority);
  const label = PRIORITIES.find((p) => p.value === task.priority)?.label ?? "No priority";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Priority of ${task.title}: ${label}`}
        data-inline="priority"
        className={cn(
          TRIGGER,
          "px-1",
          !key &&
            "text-faint opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100",
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {key ? (
          <PriorityMark priority={key} />
        ) : (
          <FlagIcon aria-hidden="true" className="size-3.5" />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Priority</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={String(task.priority)}
            onValueChange={(v) => onChange(Number(v))}
          >
            {PRIORITIES.map((p) => (
              <DropdownMenuRadioItem key={p.value} value={String(p.value)} closeOnClick>
                {p.key ? <PriorityMark priority={p.key} /> : null}
                {p.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
